<template>
  <div class="profile-container">
    <PageHeader 
      pageName="Profil" 
      :pageTitle="user?.name || 'Mon Profil'" 
      pageSubtitle="Gérez vos informations personnelles et visualisez votre activité."
    />

    <div class="profile-header">
      <div class="header-background"></div>
      <div class="profile-content">
        <div class="profile-avatar-section">
          <div class="avatar-container">
            <img :src="user?.profile_picture || 'https://placehold.co/400'" alt="Photo de profil" class="profile-avatar">
            <button class="change-avatar-btn" @click="changeAvatar" title="Changer la photo de profil">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2 2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                <circle cx="12" cy="13" r="4"></circle>
              </svg>
            </button>
          </div>
          <div class="profile-info">
            <div class="profile-badges">
              <span class="badge verified">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"/>
                </svg>
                Vérifié
              </span>
              <span class="badge premium">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"/>
                </svg>
                Premium
              </span>
            </div>
          </div>
        </div>
        <div class="profile-actions">
          <button class="btn btn-primary" @click="editProfile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Modifier le profil
          </button>
          <button class="btn btn-secondary" @click="shareProfile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
              <polyline points="16 6 12 2 8 6"></polyline>
              <line x1="12" y1="2" x2="12" y2="15"></line>
            </svg>
            Partager
          </button>
        </div>
      </div>
    </div>

    <div class="profile-body">
      <div class="profile-grid">
        <!-- Informations personnelles -->
        <div class="profile-card">
          <div class="card-header">
            <h3>Informations personnelles</h3>
            <button class="edit-btn" @click="editPersonalInfo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
          </div>
          <div class="card-content">
            <div class="info-item">
              <label>Nom complet</label>
              <span>{{ user?.name || 'Non renseigné' }}</span>
            </div>
            <div class="info-item">
              <label>Email</label>
              <span>{{ user?.email || 'Non renseigné' }}</span>
            </div>
            <div class="info-item">
              <label>Téléphone</label>
              <span>{{ user?.phone || 'Non renseigné' }}</span>
            </div>
            <div class="info-item">
              <label>Date de naissance</label>
              <span>{{ user?.birth_date || 'Non renseigné' }}</span>
            </div>
            <div class="info-item">
              <label>Localisation</label>
              <span>{{ user?.location || 'Non renseigné' }}</span>
            </div>
          </div>
        </div>

        <!-- Statistiques du compte -->
        <div class="profile-card">
          <div class="card-header">
            <h3>Statistiques</h3>
          </div>
          <div class="card-content">
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">{{ stats.chatbots || 0 }}</div>
                <div class="stat-label">Chatbots</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ stats.messages || 0 }}</div>
                <div class="stat-label">Messages</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ stats.users || 0 }}</div>
                <div class="stat-label">Utilisateurs</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ stats.uptime || '99.9%' }}</div>
                <div class="stat-label">Disponibilité</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activité récente -->
        <div class="profile-card full-width">
          <div class="card-header">
            <h3>Activité récente</h3>
            <button class="view-all-btn">Voir tout</button>
          </div>
          <div class="card-content">
            <div class="activity-list">
              <div v-for="activity in recentActivity" :key="activity.id" class="activity-item">
                <div class="activity-icon" :class="activity.type">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path v-if="activity.type === 'chatbot'" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    <path v-else-if="activity.type === 'user'" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
                    <path v-else d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.13 2 9l6.91-.74L12 2z"></path>
                  </svg>
                </div>
                <div class="activity-content">
                  <div class="activity-description">{{ activity.description }}</div>
                  <div class="activity-time">{{ formatDate(activity.date) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations du compte -->
        <div class="profile-card">
          <div class="card-header">
            <h3>Informations du compte</h3>
          </div>
          <div class="card-content">
            <div class="info-item">
              <label>Type de compte</label>
              <span class="badge premium">Premium</span>
            </div>
            <div class="info-item">
              <label>Membre depuis</label>
              <span>{{ formatDate(user?.created_at) || 'Janvier 2024' }}</span>
            </div>
            <div class="info-item">
              <label>Dernière connexion</label>
              <span>{{ formatDate(user?.last_login) || 'Aujourd\'hui' }}</span>
            </div>
            <div class="info-item">
              <label>Statut</label>
              <span class="status-indicator active">Actif</span>
            </div>
          </div>
        </div>

        <!-- Sécurité -->
        <div class="profile-card">
          <div class="card-header">
            <h3>Sécurité</h3>
            <button class="edit-btn" @click="manageSecurity">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </button>
          </div>
          <div class="card-content">
            <div class="security-item">
              <div class="security-info">
                <div class="security-title">Authentification à deux facteurs</div>
                <div class="security-description">Sécurisez votre compte avec 2FA</div>
              </div>
              <div class="security-status enabled">Activé</div>
            </div>
            <div class="security-item">
              <div class="security-info">
                <div class="security-title">Mot de passe</div>
                <div class="security-description">Dernière modification il y a 30 jours</div>
              </div>
              <button class="btn btn-small">Modifier</button>
            </div>
            <div class="security-item">
              <div class="security-info">
                <div class="security-title">Sessions actives</div>
                <div class="security-description">3 appareils connectés</div>
              </div>
              <button class="btn btn-small">Gérer</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal d'édition du profil -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Modifier le profil</h3>
          <button class="close-btn" @click="closeEditModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Nom complet</label>
            <input type="text" v-model="editForm.name" placeholder="Votre nom complet">
          </div>
          <div class="form-group">
            <label>Email</label>
            <input type="email" v-model="editForm.email" placeholder="votre@email.com">
          </div>
          <div class="form-group">
            <label>Téléphone</label>
            <input type="tel" v-model="editForm.phone" placeholder="+33 1 23 45 67 89">
          </div>
          <div class="form-group">
            <label>Localisation</label>
            <input type="text" v-model="editForm.location" placeholder="Ville, Pays">
          </div>
          <div class="form-group">
            <label>Bio</label>
            <textarea v-model="editForm.bio" placeholder="Parlez-nous de vous..." rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeEditModal">Annuler</button>
          <button class="btn btn-primary" @click="saveProfile">Sauvegarder</button>
        </div>
      </div>
    </div>

    <!-- Input file caché pour l'avatar -->
    <input type="file" ref="avatarInput" @change="handleAvatarChange" accept="image/*" style="display: none;">
  </div>
</template>

<script>
import { consoleLog } from '@/utils/logger';

export default {
  name: 'ProfileView',
  components: {
    PageHeader: () => import('@/components/PageHeader.vue')
  },
  data() {
    return {
      user: null,
      showEditModal: false,
      editForm: {
        name: '',
        email: '',
        phone: '',
        location: '',
        bio: ''
      },
      stats: {
        chatbots: 5,
        messages: 1247,
        users: 89,
        uptime: '99.9%'
      },
      recentActivity: [
        {
          id: 1,
          type: 'chatbot',
          description: 'Nouveau chatbot "Support Client" créé',
          date: new Date(Date.now() - 2 * 60 * 60 * 1000) // il y a 2h
        },
        {
          id: 2,
          type: 'user',
          description: '15 nouveaux utilisateurs se sont inscrits',
          date: new Date(Date.now() - 5 * 60 * 60 * 1000) // il y a 5h
        },
        {
          id: 3,
          type: 'achievement',
          description: 'Objectif de 1000 messages atteint',
          date: new Date(Date.now() - 24 * 60 * 60 * 1000) // il y a 1j
        },
        {
          id: 4,
          type: 'chatbot',
          description: 'Chatbot "FAQ" mis à jour',
          date: new Date(Date.now() - 48 * 60 * 60 * 1000) // il y a 2j
        }
      ]
    };
  },
  mounted() {
    this.loadUserData();
  },
  methods: {
    loadUserData() {
      // Charger les données utilisateur depuis localStorage ou API
      const userData = localStorage.getItem('user');
      if (userData) {
        this.user = JSON.parse(userData).data || JSON.parse(userData);
        this.editForm = {
          name: this.user.name || '',
          email: this.user.email || '',
          phone: this.user.phone || '',
          location: this.user.location || '',
          bio: this.user.bio || ''
        };
      }
    },
    editProfile() {
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    saveProfile() {
      // Sauvegarder les modifications
      Object.assign(this.user, this.editForm);
      
      // Mettre à jour localStorage
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData.data) {
        userData.data = { ...userData.data, ...this.editForm };
      } else {
        Object.assign(userData, this.editForm);
      }
      localStorage.setItem('user', JSON.stringify(userData));
      
      this.closeEditModal();
      this.showNotification('Profil mis à jour avec succès', 'success');
    },
    changeAvatar() {
      this.$refs.avatarInput.click();
    },
    handleAvatarChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.user.profile_picture = e.target.result;
          // Dans une vraie application, on uploadrait l'image sur un serveur
          this.showNotification('Photo de profil mise à jour', 'success');
        };
        reader.readAsDataURL(file);
      }
    },
    shareProfile() {
      // Fonctionnalité de partage
      if (navigator.share) {
        navigator.share({
          title: 'Mon profil Peelo',
          text: 'Découvrez mon profil sur Peelo',
          url: window.location.href
        });
      } else {
        // Fallback pour copier le lien
        navigator.clipboard.writeText(window.location.href);
        this.showNotification('Lien copié dans le presse-papiers', 'success');
      }
    },
    editPersonalInfo() {
      this.editProfile();
    },
    manageSecurity() {
      // Rediriger vers les paramètres de sécurité
      this.$router.push('/dashboard/account-settings?tab=security');
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) return 'Il y a quelques minutes';
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      if (diffDays < 7) return `Il y a ${diffDays}j`;
      
      return d.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    },
    showNotification(message, type = 'success') {
      // Émettre une notification (à implémenter selon votre système de notifications)
      consoleLog(`${type}: ${message}`);
    }
  }
};
</script>

<style scoped>
.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
}

.profile-header {
  position: relative;
  margin-bottom: 2rem;
}

.header-background {
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  position: relative;
}

.profile-content {
  position: absolute;
  bottom: -60px;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 2rem;
}

.profile-avatar-section {
  display: flex;
  align-items: flex-end;
  gap: 1.5rem;
}

.avatar-container {
  position: relative;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  object-fit: cover;
}

.change-avatar-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e2e8f0;
  color: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.change-avatar-btn:hover {
  background: #667eea;
  color: white;
  transform: scale(1.1);
}

.profile-info {
  padding-bottom: 1rem;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.profile-email {
  font-size: 1.1rem;
  color: #718096;
  margin: 0 0 1rem 0;
}

.profile-badges {
  display: flex;
  gap: 0.75rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.badge.verified {
  background: #e6fffa;
  color: #319795;
}

.badge.premium {
  background: #fef5e7;
  color: #d69e2e;
}

.profile-actions {
  display: flex;
  gap: 1rem;
  padding-bottom: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
  transform: translateY(-2px);
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.profile-body {
  margin-top: 80px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
}

.profile-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.profile-card.full-width {
  grid-column: 1 / -1;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.edit-btn,
.view-all-btn {
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.edit-btn:hover,
.view-all-btn:hover {
  color: #5a67d8;
}

.card-content {
  padding: 1.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f7fafc;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item label {
  font-weight: 500;
  color: #4a5568;
}

.info-item span {
  color: #1a202c;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 12px;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.activity-icon.chatbot {
  background: #667eea;
}

.activity-icon.user {
  background: #48bb78;
}

.activity-icon.achievement {
  background: #ed8936;
}

.activity-content {
  flex: 1;
}

.activity-description {
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.activity-time {
  font-size: 0.875rem;
  color: #718096;
}

.security-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #f7fafc;
}

.security-item:last-child {
  border-bottom: none;
}

.security-info {
  flex: 1;
}

.security-title {
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.security-description {
  font-size: 0.875rem;
  color: #718096;
}

.security-status {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.security-status.enabled {
  background: #e6fffa;
  color: #319795;
}

.status-indicator {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-indicator.active {
  background: #e6fffa;
  color: #319795;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
}

.close-btn:hover {
  background: #f7fafc;
  color: #4a5568;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .profile-container {
    padding: 1rem;
  }
  
  .profile-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .profile-actions {
    width: 100%;
  }
  
  .profile-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style> 